/**
 * Google Consent Mode v2.
 *
 * Every Google tag in GTM (Google Ads, GA4, conversion linker) reads these
 * consent states. We run "advanced" consent mode: the tags always load, but
 * until a visitor accepts they send cookieless pings only. Google uses those
 * pings to model the conversions and visits of visitors who decline, so Google
 * Ads keeps optimising on (close to) all sales instead of only on the visitors
 * who accepted. Accepting turns on cookies, remarketing and enhanced
 * conversions.
 *
 * The default must be pushed to the dataLayer before GTM loads. That happens in
 * useTagManager, which calls setConsentDefault() right before initialising GTM.
 */

export const ConsentCookieName = 'ATHENASTUDIES-CONSENT';

// Bump when the categories or the banner text change in a way that requires
// asking everyone again.
const ConsentVersion = 1;

// The Dutch regulator (AP) considers asking again after a year reasonable.
const ConsentCookieSeconds = 365 * 24 * 60 * 60;

export const ConsentChangeEvent = 'athena-consent-change';
export const ConsentOpenEvent = 'athena-consent-open';
export const ConsentSettingsHash = '#cookie-instellingen';

function isBrowser(){
	return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * gtag() as Google defines it. It has to push the arguments object, not an
 * array, otherwise GTM ignores consent commands.
 */
export function gtag(){
	if(!isBrowser()) return;

	window.dataLayer = window.dataLayer || [];
	// eslint-disable-next-line prefer-rest-params
	window.dataLayer.push(arguments);
}

/**
 * @param {{analytics: boolean, marketing: boolean}} choice
 * @returns {object} The Consent Mode v2 state for this choice
 */
export function consentState(choice){
	const analytics = Boolean(choice && choice.analytics);
	const marketing = Boolean(choice && choice.marketing);

	return {
		ad_storage: marketing ? 'granted' : 'denied',
		ad_user_data: marketing ? 'granted' : 'denied',
		ad_personalization: marketing ? 'granted' : 'denied',
		analytics_storage: analytics ? 'granted' : 'denied',
		functionality_storage: 'granted',
		personalization_storage: marketing ? 'granted' : 'denied',
		security_storage: 'granted',
	};
}

/**
 * @returns {{analytics: boolean, marketing: boolean, date: string}|null} The stored choice, or null when the visitor has not chosen yet
 */
export function readConsent(){
	if(!isBrowser()) return null;

	const entry = document.cookie
		.split('; ')
		.find((part) => part.indexOf(`${ConsentCookieName}=`) === 0);

	if(!entry) return null;

	try {
		const value = JSON.parse(decodeURIComponent(entry.slice(ConsentCookieName.length + 1)));

		if(!value || value.v !== ConsentVersion) return null;

		return {
			analytics: Boolean(value.a),
			marketing: Boolean(value.m),
			date: value.d,
		};
	} catch(e){
		return null;
	}
}

function writeConsent(choice){
	const value = encodeURIComponent(JSON.stringify({
		v: ConsentVersion,
		a: choice.analytics ? 1 : 0,
		m: choice.marketing ? 1 : 0,
		d: new Date().toISOString(),
	}));
	const secure = window.location.protocol === 'https:' ? '; Secure' : '';

	document.cookie = `${ConsentCookieName}=${value}; Max-Age=${ConsentCookieSeconds}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Pushes the consent default (everything optional denied) and, for a returning
 * visitor, their stored choice. Must run before GTM is initialised.
 */
export function setConsentDefault(){
	if(!isBrowser()) return;

	gtag('consent', 'default', {
		...consentState({ analytics: false, marketing: false }),
		// Give the banner a moment to restore a stored choice before tags fire.
		wait_for_update: 500,
	});

	// Without ad_storage, strip ad identifiers from requests...
	gtag('set', 'ads_data_redaction', true);
	// ...but pass the click identifiers through the URLs, so a declined visit
	// can still be attributed to its Google Ads click.
	gtag('set', 'url_passthrough', true);

	const stored = readConsent();

	if(stored){
		gtag('consent', 'update', consentState(stored));
	}
}

/**
 * Stores the visitor's choice and tells the Google tags about it.
 *
 * @param {{analytics: boolean, marketing: boolean}} choice
 */
export function saveConsent(choice){
	if(!isBrowser()) return;

	writeConsent(choice);
	gtag('consent', 'update', consentState(choice));

	window.dataLayer.push({
		event: 'consent_update',
		consent_analytics: choice.analytics ? 'granted' : 'denied',
		consent_marketing: choice.marketing ? 'granted' : 'denied',
	});

	window.dispatchEvent(new CustomEvent(ConsentChangeEvent, { detail: choice }));
}

/**
 * Re-opens the banner with the preferences visible, e.g. from the footer.
 */
export function openConsentSettings(){
	if(!isBrowser()) return;

	window.dispatchEvent(new CustomEvent(ConsentOpenEvent));
}
